import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Upload, CheckCircle, AlertCircle } from 'lucide-react';
import Papa from 'papaparse';
import { processLogbookWithErrors } from '@utils/logbookUtils.js';
import RouteSearch from './RouteSearch.js';

const CompactHeader = ({
  onDataLoaded,
  data,
  processingErrors = [],
  setProcessingErrors,
  hideUpload = false,
  climberName = null,
  lockedStyle = null,
  lockedGradeSystem = null,
  lockedYear = null,
  filters = {},
  onFiltersChange = {},
  onSearch = () => {}
}) => {
  const {
    selectedYear = 'all',
    selectedCrag = 'all',
    selectedCountry = 'all',
    years = [],
    crags = [],
    countries = [],
    climbingStyle = 'sport',
    selectedMetric = 'grades',
    gradeSystem = 'ewbank'
  } = filters;

  const {
    onYearChange = () => {},
    onCragChange = () => {},
    onCountryChange = () => {},
    onStyleChange = () => {},
    onMetricChange = () => {},
    onGradeSystemChange = () => {}
  } = onFiltersChange;
  
  const [isUploadExpanded, setIsUploadExpanded] = useState(!data);
  const [filename, setFilename] = useState(null);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFilename(file.name);
    if (!file.name.endsWith('.csv')) {
      alert('Please upload a CSV file exported from theCrag.');
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: function(results) {
        try {
          const { processed, errors } = processLogbookWithErrors(results.data);
          if (!processed || processed.length === 0) {
            throw new Error('No valid data after processing');
          }
          
          // Add filename to each record
          const dataWithFilename = processed.map(record => ({
            ...record,
            filename: file.name
          }));
          
          onDataLoaded({
            data: dataWithFilename,
            processingErrors: errors
          });
          setIsUploadExpanded(false);
        } catch (error) {
          alert('Error processing data: ' + error.message);
        }
      },
      error: function(error) {
        alert('Error reading file: ' + error.message);
      }
    });
  };

  React.useEffect(() => {
    if (data?.length > 0 && data[0]?.filename) {
      setFilename(data[0].filename);
    }
  }, [data]);

  const getDisplayFilename = (filename) => {
    if (!filename) return 'Upload Logbook';
    return filename.replace(/\.csv$/i, '');
  };

  return (
    <div className="bg-white shadow rounded-lg w-full">
      <div className="p-3 flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          {hideUpload && data ? (
            // Show data summary when loaded from URL
            <div className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700">
              <span className="font-semibold text-gray-900">
                {climberName ? `${climberName} Climbing Dashboard` : 'Climbing Dashboard'}
              </span>
            </div>
          ) : (
            <button
              onClick={() => setIsUploadExpanded(!isUploadExpanded)}
              className="w-full flex items-center gap-2 px-4 py-2 rounded border hover:bg-gray-50"
            >
              {data ? (
                <div className="flex items-center gap-2">
                  <CheckCircle className="text-green-600 shrink-0" size={16} />
                  {processingErrors.length > 0 && (
                    <span className="bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded-full">
                      {processingErrors.length} errors
                    </span>
                  )}
                </div>
              ) : (
                <Upload className="text-gray-600 shrink-0" size={16} />
              )}
              <span className="text-sm truncate">
                {data ? getDisplayFilename(filename) : 'Upload Logbook'}
              </span>
              {isUploadExpanded ?
                <ChevronUp className="shrink-0 ml-auto" size={16} /> :
                <ChevronDown className="shrink-0 ml-auto" size={16} />
              }
            </button>
          )}

          {isUploadExpanded && !hideUpload && (
            <div className="absolute top-full left-0 mt-1 w-[400px] bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50">
              <div 
                className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-gray-400"
                onClick={() => document.getElementById('file-input').click()}
              >
                <p className="text-sm mb-1">Click to upload or drag and drop</p>
                <p className="text-xs text-gray-500">CSV file exported from theCrag</p>
                <input
                  id="file-input"
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </div>

              {processingErrors.length > 0 && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="text-red-500" size={16} />
                      <h3 className="text-sm font-medium text-red-900">Processing Exceptions</h3>
                    </div>
                    <button
                      onClick={() => setProcessingErrors([])}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  </div>
                  <p className="text-sm text-red-800 mb-2">{processingErrors.length} ascents couldn't be processed:</p>
                  <div className="max-h-40 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-red-900">
                          <th className="text-left py-1 font-medium">Route</th>
                          <th className="text-left py-1 font-medium">Date</th>
                          <th className="text-left py-1 font-medium">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {processingErrors.map((error, index) => (
                          <tr key={index} className="border-t border-red-200">
                            <td className="py-1 text-red-800">{error.route}</td>
                            <td className="py-1 text-red-800">{error.date}</td>
                            <td className="py-1 text-red-800">{error.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex-1">
          <RouteSearch data={data} onSearch={onSearch} />
        </div>
      </div>

      <div className="px-3 pb-3 pt-0 border-t flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-1">
          {!lockedYear ? (
            <select
              className="h-9 px-2 text-sm border rounded min-w-[120px]"
              value={selectedYear}
              onChange={(e) => onYearChange(e.target.value)}
            >
              <option value="all">All Time</option>
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          ) : (
            <span className="h-9 px-2 text-sm flex items-center text-gray-700">
              {lockedYear === 'all' ? 'All Time' : lockedYear}
            </span>
          )}

          {!lockedStyle ? (
            <select
              className="h-9 px-2 text-sm border rounded min-w-[100px]"
              value={climbingStyle}
              onChange={(e) => onStyleChange(e.target.value)}
            >
              <option value="sport">Sport</option>
              <option value="trad">Trad</option>
              <option value="boulder">Boulder</option>
            </select>
          ) : (
            <span className="h-9 px-2 text-sm flex items-center text-gray-700 capitalize">
              {lockedStyle}
            </span>
          )}

          {!lockedGradeSystem && climbingStyle !== 'boulder' ? (
            <select
              className="h-9 px-2 text-sm border rounded min-w-[100px]"
              value={gradeSystem}
              onChange={(e) => onGradeSystemChange(e.target.value)}
            >
              <option value="ewbank">Ewbank</option>
              <option value="french">French</option>
              <option value="yds">YDS</option>
            </select>
          ) : !lockedGradeSystem && climbingStyle === 'boulder' ? null : (
            <span className="h-9 px-2 text-sm flex items-center text-gray-700 capitalize">
              {lockedGradeSystem}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-1">
          <select 
            className="h-9 px-2 text-sm border rounded flex-1"
            value={selectedCrag}
            onChange={(e) => onCragChange(e.target.value)}
          >
            <option value="all">All Crags</option>
            {crags.map(crag => (
              <option key={crag.name} value={crag.name}>
                {crag.name} ({crag.count})
              </option>
            ))}
          </select>

          <select 
            className="h-9 px-2 text-sm border rounded flex-1"
            value={selectedCountry}
            onChange={(e) => onCountryChange(e.target.value)}
          >
            <option value="all">All Countries</option>
            {countries.map(country => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default CompactHeader;